/* eslint-disable no-await-in-loop */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

const ScannerContext = createContext();
const VENDOR_IDS = [0x05E0, 0x05F9];
const SERIAL_IDLE_FLUSH_MS = 100;

export const useScanner = () => useContext(ScannerContext);

// Ported from Dropzone: HID reports and 115200-baud serial scanners share a page listener.
function ScannerProvider({ children }) {
  const connection = useRef(null);
  const connecting = useRef(false);
  const listener = useRef(null);
  const defaultListener = useRef(null);
  const mounted = useRef(true);
  const [connected, setConnected] = useState(false);
  const [busy, setBusy] = useState(false);
  const supported = !!(navigator.hid || navigator.serial);

  const deliver = useCallback((value) => {
    if (document.visibilityState === 'hidden') return;
    const label = value.replace('%P1.2%V', '').trim();
    const callback = listener.current || defaultListener.current;
    if (label && callback) callback(label);
  }, []);

  const destroyReader = useCallback(async () => {
    const { current } = connection;
    if (!current) return;
    connection.current = null;
    current.cancelled = true;
    window.clearTimeout(current.timer);
    if (mounted.current) setConnected(false);
    try {
      if (current.kind === 'hid') {
        current.device.removeEventListener('inputreport', current.onReport);
        await current.device.close();
      } else {
        if (current.reader) await current.reader.cancel();
        if (current.readPromise) await current.readPromise;
        await current.device.close();
      }
    } catch (error) {
      // Unplugged devices may already have closed their streams.
    }
  }, []);

  const attach = useCallback(async (device, kind) => {
    if (kind === 'hid') {
      if (!device.opened) await device.open();
    } else {
      await device.open({ baudRate: 115200 });
    }
    if (!mounted.current) {
      await device.close();
      return;
    }
    const current = {
      device, kind, buffer: '', cancelled: false, timer: null,
    };
    connection.current = current;
    setConnected(true);

    if (kind === 'hid') {
      current.onReport = ({ data }) => {
        for (let i = 0; i < data.byteLength; i += 1) {
          const code = data.getUint8(i);
          // Preserve Dropzone's scanner report filtering, including its '?' exclusion.
          if ((code === 10 || (code >= 32 && code <= 126)) && code !== 63) {
            current.buffer += String.fromCharCode(code);
          }
        }
        const labels = current.buffer.split('\n');
        current.buffer = labels.pop();
        labels.forEach(deliver);
      };
      device.addEventListener('inputreport', current.onReport);
      return;
    }

    current.readPromise = (async () => {
      const decoder = new TextDecoder('utf-8');
      try {
        current.reader = device.readable.getReader();
        while (!current.cancelled) {
          const { value, done } = await current.reader.read();
          if (current.cancelled || done) break;
          current.buffer += decoder.decode(value, { stream: true });
          const labels = current.buffer.split(/\r\n|\r|\n/);
          current.buffer = labels.pop();
          labels.forEach(deliver);
          window.clearTimeout(current.timer);
          if (current.buffer) {
            current.timer = window.setTimeout(() => {
              const label = current.buffer;
              current.buffer = '';
              if (!current.cancelled) deliver(label);
            }, SERIAL_IDLE_FLUSH_MS);
          }
          let ackEnabled = false;
          try {
            ackEnabled = window.localStorage.getItem('scannerSerialAckEnabled') === 'true';
          } catch (error) {
            // Storage may be disabled by browser policy.
          }
          if (ackEnabled && device.writable) {
            const writer = device.writable.getWriter();
            try {
              await writer.write(new Uint8Array([0x06]));
            } finally {
              writer.releaseLock();
            }
          }
        }
      } catch (error) {
        if (!current.cancelled && mounted.current) {
          toastr.error(error.message, 'Scanner disconnected');
        }
      } finally {
        window.clearTimeout(current.timer);
        if (current.reader) current.reader.releaseLock();
        if (connection.current === current) {
          connection.current = null;
          if (mounted.current) setConnected(false);
          try {
            await device.close();
          } catch (error) {
            // A physically disconnected port may already be closed.
          }
        }
      }
    })();
  }, [deliver]);

  const connect = useCallback(async (reconnect, cb) => {
    if (connection.current || connecting.current) return;
    connecting.current = true;
    if (mounted.current) setBusy(true);
    try {
      let devices = [];
      if (navigator.hid) {
        devices = reconnect
          ? (await navigator.hid.getDevices()).filter(
            (device) => VENDOR_IDS.includes(device.vendorId),
          )
          : await navigator.hid.requestDevice({
            filters: VENDOR_IDS.map((vendorId) => ({ vendorId })),
          });
      }
      let device = devices[0];
      const kind = device ? 'hid' : 'serial';
      if (!device && navigator.serial) {
        device = reconnect
          ? (await navigator.serial.getPorts())[0]
          : await navigator.serial.requestPort();
      }
      if (!device) return;
      await attach(device, kind);
      if (!mounted.current) return;
      if (typeof cb === 'function') cb(device);
      if (!reconnect) toastr.success(device.productName || 'Serial scanner', 'Scanner connected');
    } catch (error) {
      if (!reconnect && error.name !== 'NotFoundError') {
        toastr.error(error.message, 'Failed to connect to scanner');
      }
    } finally {
      connecting.current = false;
      if (mounted.current) setBusy(false);
    }
  }, [attach]);

  const connectScanner = useCallback((cb) => connect(false, cb), [connect]);
  const reconnectScanner = useCallback((cb) => connect(true, cb), [connect]);
  const isConnected = useCallback(() => connected, [connected]);
  const registerListener = useCallback((handler) => { listener.current = handler; }, []);
  const removeListener = useCallback((handler) => {
    if (!handler || listener.current === handler) listener.current = null;
  }, []);
  const registerDefaultListener = useCallback((handler) => {
    defaultListener.current = handler;
    return () => {
      if (defaultListener.current === handler) defaultListener.current = null;
    };
  }, []);

  useEffect(() => {
    mounted.current = true;
    const onDisconnect = (event) => {
      const { current } = connection;
      if (current && (event.device === current.device || event.target === current.device
        || event.port === current.device)) destroyReader();
    };
    if (navigator.hid) navigator.hid.addEventListener('disconnect', onDisconnect);
    if (navigator.serial) navigator.serial.addEventListener('disconnect', onDisconnect);
    return () => {
      mounted.current = false;
      if (navigator.hid) navigator.hid.removeEventListener('disconnect', onDisconnect);
      if (navigator.serial) navigator.serial.removeEventListener('disconnect', onDisconnect);
      destroyReader();
    };
  }, [destroyReader]);

  const value = useMemo(() => ({
    connectScanner,
    reconnectScanner,
    isConnected,
    destroyReader,
    registerListener,
    removeListener,
    registerDefaultListener,
    supported,
    busy,
  }), [connectScanner, reconnectScanner, isConnected, destroyReader,
    registerListener, removeListener, registerDefaultListener, supported, busy]);

  return <ScannerContext.Provider value={value}>{children}</ScannerContext.Provider>;
}

ScannerProvider.propTypes = { children: PropTypes.node.isRequired };
export default ScannerProvider;
